import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { logger } from '../utils/logger';
import toast from 'react-hot-toast';
import { normalizeStoreProfile } from '../utils/storeUtils';
import { fetchStoreListPage } from '../services/storeListApi';

const AmazonProfileContext = createContext();

export const useAmazonProfile = () => {
  const context = useContext(AmazonProfileContext);
  if (!context) {
    throw new Error('useAmazonProfile must be used within an AmazonProfileProvider');
  }
  return context;
};

export const AmazonProfileProvider = ({ children }) => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [profilesNextCursor, setProfilesNextCursor] = useState(null);
  const [profilesHasMore, setProfilesHasMore] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(() => {
    const savedProfile = localStorage.getItem('selectedAmazonProfile');
    return savedProfile ? JSON.parse(savedProfile) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      loadProfiles();
    } else {
      setProfiles([]);
      setProfilesNextCursor(null);
      setProfilesHasMore(false);
      setSelectedProfile(null);
      localStorage.removeItem('selectedAmazonProfile');
    }
  }, [user]);

  useEffect(() => {
    if (selectedProfile) {
      localStorage.setItem('selectedAmazonProfile', JSON.stringify(selectedProfile));
    }
  }, [selectedProfile]);

  const applySelectionAfterLoad = useCallback((accessibleProfiles) => {
    const savedProfile = localStorage.getItem('selectedAmazonProfile');
    if (savedProfile && accessibleProfiles.length > 0) {
      const parsed = JSON.parse(savedProfile);
      const existingProfile = accessibleProfiles.find(
        (p) => String(p.profileId) === String(parsed.profileId)
      );

      if (existingProfile) {
        setSelectedProfile(existingProfile);
      } else {
        const preferred = accessibleProfiles.find((p) => p.isConnected) || accessibleProfiles[0];
        setSelectedProfile(preferred);
        toast('Previously selected store is no longer available. Switched stores.', { icon: 'ℹ️' });
      }
    } else if (accessibleProfiles.length > 0) {
      const preferred = accessibleProfiles.find((p) => p.isConnected) || accessibleProfiles[0];
      setSelectedProfile((prev) => prev || preferred);
    }
  }, []);

  const loadProfiles = async () => {
    if (!user) {
      logger.debug('🔒 Cannot load profiles: user not authenticated');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      logger.debug('🔄 Loading stores (paginated)...');

      const page = await fetchStoreListPage({
        limit: 100,
        includeDisconnected: true,
      });

      setProfiles(page.items);
      setProfilesNextCursor(page.nextCursor);
      setProfilesHasMore(page.hasMore);
      logger.debug(`✅ Loaded ${page.items.length} stores (hasMore=${page.hasMore})`);

      applySelectionAfterLoad(page.items);
    } catch (err) {
      console.error('❌ Failed to load stores:', err);
      setError(err.message);
      toast.error(`Failed to load stores: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreProfiles = useCallback(async () => {
    if (!profilesNextCursor || !user) return { added: 0 };
    try {
      const page = await fetchStoreListPage({
        limit: 100,
        cursor: profilesNextCursor,
        includeDisconnected: true,
      });
      setProfiles((prev) => {
        const seen = new Set(prev.map((p) => String(p.profileId)));
        const merged = [...prev];
        page.items.forEach((p) => {
          if (!seen.has(String(p.profileId))) merged.push(p);
        });
        return merged;
      });
      setProfilesNextCursor(page.nextCursor);
      setProfilesHasMore(page.hasMore);
      return { added: page.items.length };
    } catch (err) {
      toast.error(err.message || 'Failed to load more stores');
      return { added: 0 };
    }
  }, [profilesNextCursor, user]);

  const searchStores = useCallback(async (q, { limit = 50 } = {}) => {
    const page = await fetchStoreListPage({
      q: String(q || '').trim(),
      limit,
      includeDisconnected: true,
    });
    return page.items;
  }, []);

  const selectProfile = (profile) => {
    setSelectedProfile(profile ? normalizeStoreProfile(profile) : null);
  };

  const getProfileById = (profileId) => {
    return profiles.find((profile) => String(profile.profileId) === String(profileId));
  };

  const refreshProfiles = () => {
    if (!user) return Promise.resolve();
    return loadProfiles();
  };

  const hasProfiles = profiles.length > 0;
  const hasSelectedProfile = selectedProfile !== null;

  const value = {
    profiles,
    profilesNextCursor,
    profilesHasMore,
    selectedProfile,
    loading,
    error,
    hasProfiles,
    hasSelectedProfile,
    selectProfile,
    getProfileById,
    refreshProfiles,
    loadProfiles,
    loadMoreProfiles,
    searchStores,
    selectedProfileId: selectedProfile?.profileId,
    selectedProfileName: selectedProfile?.name,
    selectedProfileMarketplace: selectedProfile?.marketplace || selectedProfile?.countryCode,
  };

  return (
    <AmazonProfileContext.Provider value={value}>
      {children}
    </AmazonProfileContext.Provider>
  );
};

export default AmazonProfileContext;
